/**
 * Sync engine: push the outbox in small batches, pull per-project deltas,
 * then process the media queue.
 *
 * Triggers: app open, the browser's `online` event, an interval while the tab
 * is visible, returning to the tab, newly queued work (debounced), and the
 * manual "Sync now". Background Sync is not relied on (Safari/Firefox lack it).
 *
 * Invariants:
 *  - one run at a time; a trigger during a run schedules exactly one re-run;
 *  - auth comes first: on reconnect the token is refreshed before any push;
 *  - nothing is ever dropped for being offline or signed out — unreachable or
 *    401 puts the batch back to `pending` and stops;
 *  - conflicts and rejections stay in the outbox, visible in the Sync Center,
 *    until the person resolves them.
 */
import { useSyncExternalStore } from 'react';

import { isApiError, NETWORK_ERROR } from '@/lib/api/errors';
import type { PushMutation, PushResult } from '@/lib/api/types';
import { applierTables, applyChange, applyPushedEntity } from '@/lib/db/appliers';
import { getCursor, setCursor } from '@/lib/db/cursors';
import { db, type OutboxRecord } from '@/lib/db/db';
import { getValue, setValue } from '@/lib/db/kv';
import { pruneMessages } from '@/lib/db/messages';
import { nextMediaAttemptAt, pendingMediaCount, subscribeToMediaQueue } from '@/lib/db/media-queue';
import * as outbox from '@/lib/db/outbox';

import type { SyncApi } from './api';

export const PUSH_BATCH_SIZE = 25;
const MAX_PUSH_BATCHES_PER_RUN = 20;
const PULL_PAGE_SIZE = 200;
const MAX_PULL_PAGES_PER_PROJECT = 25;
const LAST_SUCCESS_KEY = 'sync.last_success_at';

export type SyncCounts = {
  pending: number;
  in_flight: number;
  failed: number;
  conflict: number;
  rejected: number;
  media: number;
};

export type SyncSnapshot = {
  running: boolean;
  connectivity: 'online' | 'offline';
  authRequired: boolean;
  counts: SyncCounts;
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  lastError: { code: string; message: string } | null;
};

export type RunOutcome = 'ok' | 'offline' | 'auth_required' | 'error' | 'skipped';

export type SyncEngineDeps = {
  api: SyncApi;
  ensureOnline: () => Promise<'ok' | 'offline' | 'auth_required'>;
  projectIds: () => Promise<string[]>;
  deviceId: () => string;
  /** Platform and app version reported with each push (sync-health telemetry). */
  deviceInfo?: () => { platform: string; app_version: string };
  /** Upload queued media (runs after push + pull, so records exist before their photos). */
  processMedia?: () => Promise<unknown>;
  now?: () => number;
};

const EMPTY_COUNTS: SyncCounts = {
  pending: 0,
  in_flight: 0,
  failed: 0,
  conflict: 0,
  rejected: 0,
  media: 0,
};

function toWire(record: OutboxRecord): PushMutation {
  return {
    mutation_id: record.mutation_id,
    entity_type: record.entity_type,
    entity_id: record.entity_id,
    operation: record.operation,
    base_version: record.base_version,
    payload: record.payload,
    created_at_device: record.created_at_device,
    project_id: record.project_id,
  };
}

class StopRun extends Error {
  constructor(readonly outcome: 'offline' | 'auth_required') {
    super(outcome);
  }
}

export class SyncEngine {
  private snapshot: SyncSnapshot = {
    running: false,
    connectivity: 'online',
    authRequired: false,
    counts: EMPTY_COUNTS,
    lastSuccessAt: null,
    lastAttemptAt: null,
    lastError: null,
  };
  private readonly listeners = new Set<() => void>();
  private current: Promise<RunOutcome> | null = null;
  private rerunRequested = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private readonly now: () => number;

  constructor(private readonly deps: SyncEngineDeps) {
    this.now = deps.now ?? Date.now;
  }

  // -- store ---------------------------------------------------------------

  getSnapshot = (): SyncSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private update(patch: Partial<SyncSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  async refreshCounts(): Promise<void> {
    try {
      const [byState, media] = await Promise.all([outbox.countByState(), pendingMediaCount()]);
      this.update({
        counts: {
          pending: byState.pending,
          in_flight: byState.in_flight,
          failed: byState.failed,
          conflict: byState.conflict,
          rejected: byState.rejected,
          media,
        },
      });
    } catch {
      // Database unavailable (e.g. closed during logout); keep the last counts.
    }
  }

  async hydrate(): Promise<void> {
    const lastSuccessAt = (await getValue<string>(LAST_SUCCESS_KEY).catch(() => undefined)) ?? null;
    this.update({ lastSuccessAt });
    await this.refreshCounts();
  }

  setConnectivity(connectivity: 'online' | 'offline'): void {
    if (this.snapshot.connectivity !== connectivity) this.update({ connectivity });
  }

  /** Forget everything (after logout). */
  reset(): void {
    this.update({
      counts: EMPTY_COUNTS,
      lastSuccessAt: null,
      lastAttemptAt: null,
      lastError: null,
      authRequired: false,
    });
  }

  // -- running -------------------------------------------------------------

  /** Run now (or join the run in progress and schedule one more after it). */
  run(): Promise<RunOutcome> {
    if (this.current) {
      this.rerunRequested = true;
      return this.current;
    }
    this.current = this.execute().finally(() => {
      this.current = null;
      if (this.rerunRequested) {
        this.rerunRequested = false;
        void this.run();
      }
    });
    return this.current;
  }

  /** Coalesce bursts of local writes into one sync shortly after. */
  schedule(delayMs = 1500): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.run();
    }, delayMs);
  }

  private async execute(): Promise<RunOutcome> {
    this.update({ running: true, lastAttemptAt: new Date(this.now()).toISOString() });
    try {
      const auth = await this.deps.ensureOnline();
      if (auth === 'offline') throw new StopRun('offline');
      if (auth === 'auth_required') throw new StopRun('auth_required');
      this.update({ connectivity: 'online', authRequired: false });

      await outbox.recoverInFlight();
      const pushError = await this.push();
      await this.pull();
      await this.deps.processMedia?.();

      await this.scheduleRetry();
      if (pushError) {
        this.update({ lastError: pushError });
        return 'error';
      }
      const at = new Date(this.now()).toISOString();
      await setValue(LAST_SUCCESS_KEY, at);
      this.update({ lastSuccessAt: at, lastError: null });
      return 'ok';
    } catch (error) {
      if (error instanceof StopRun) {
        if (error.outcome === 'offline') this.update({ connectivity: 'offline' });
        else this.update({ authRequired: true });
        return error.outcome;
      }
      if (isApiError(error) && error.isNetwork) {
        this.update({ connectivity: 'offline' });
        return 'offline';
      }
      if (isApiError(error) && error.needsAuth) {
        this.update({ authRequired: true });
        return 'auth_required';
      }
      this.update({
        lastError: {
          code: isApiError(error) ? error.code : 'SYNC_FAILED',
          message: error instanceof Error ? error.message : 'Sync failed',
        },
      });
      return 'error';
    } finally {
      await this.refreshCounts();
      this.update({ running: false });
    }
  }

  /**
   * Wake up when the soonest backed-off mutation or upload becomes due, so a
   * "retry in 10 s" happens in 10 s rather than at the next interval tick.
   * Only times still in the future count: an item already due but not sent
   * (held for Wi-Fi, waiting on its record) must not spin the engine.
   */
  private async scheduleRetry(): Promise<void> {
    if (!this.started) return;
    const times = (
      await Promise.all([
        outbox.nextAttemptAt().catch(() => null),
        nextMediaAttemptAt().catch(() => null),
      ])
    ).filter((time): time is number => time !== null && time > this.now());
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (!times.length) return;
    this.retryTimer = setTimeout(
      () => {
        this.retryTimer = null;
        void this.run();
      },
      Math.min(...times) - this.now() + 250,
    );
  }

  /**
   * Push queued mutations. Returns a non-fatal error to surface (a batch the
   * server refused as a whole), throws to stop the run (offline / auth).
   */
  private async push(): Promise<{ code: string; message: string } | null> {
    let surfaced: { code: string; message: string } | null = null;

    for (let round = 0; round < MAX_PUSH_BATCHES_PER_RUN; round += 1) {
      const batch = await outbox.takeBatch(PUSH_BATCH_SIZE, this.now());
      if (!batch.length) break;
      const ids = batch.map((record) => record.mutation_id);
      await outbox.markInFlight(ids);

      let results: PushResult[];
      try {
        const pendingCount = await outbox.unsyncedCount().catch(() => undefined);
        const response = await this.deps.api.push(
          {
            device_id: this.deps.deviceId(),
            ...this.deps.deviceInfo?.(),
            ...(pendingCount === undefined ? {} : { pending_count: pendingCount }),
            mutations: batch.map(toWire),
          },
          // Same batch → same key, so a retried request is the same intent.
          `sync-push:${ids[0]}:${ids.length}:${ids[ids.length - 1]}`,
        );
        results = response.results;
      } catch (error) {
        if (isApiError(error) && (error.isNetwork || error.needsAuth)) {
          await outbox.returnToPending(ids);
          throw new StopRun(error.isNetwork ? 'offline' : 'auth_required');
        }
        if (isApiError(error) && error.code === 'ENTITLEMENT_REQUIRED') {
          // Build access lapsed (402): the work is fine, the account is not.
          // Keep everything queued; /me explains on the next refresh.
          await outbox.returnToPending(ids);
          throw error;
        }
        const reason = {
          code: isApiError(error) ? error.code : NETWORK_ERROR,
          message: error instanceof Error ? error.message : 'Push failed',
        };
        await outbox.markFailed(ids, reason, this.now());
        surfaced = reason;
        break;
      }

      const answered = new Set<string>();
      for (const result of results) {
        if (!ids.includes(result.mutation_id)) continue;
        answered.add(result.mutation_id);
        await this.applyPushResult(batch, result);
      }
      const unanswered = ids.filter((id) => !answered.has(id));
      if (unanswered.length) await outbox.returnToPending(unanswered);
      if (unanswered.length === ids.length) break; // server made no progress; avoid spinning
    }
    return surfaced;
  }

  private async applyPushResult(batch: OutboxRecord[], result: PushResult): Promise<void> {
    const record = batch.find((entry) => entry.mutation_id === result.mutation_id);
    if (!record) return;
    // A resend is answered with the first answer. A duplicate of a conflict
    // or rejection is still a conflict or rejection — never "done".
    const status =
      result.status === 'duplicate' &&
      result.original_status &&
      result.original_status !== 'applied'
        ? result.original_status
        : result.status;
    switch (status) {
      case 'applied':
      case 'duplicate':
        await db.transaction('rw', applierTables(), async () => {
          await outbox.complete(record.mutation_id);
          await applyPushedEntity(
            record.entity_type,
            record.entity_id,
            result.entity_version,
            result.data,
            record.project_id,
          );
        });
        break;
      case 'conflict':
        await outbox.markConflict(record.mutation_id, result.conflict ?? null);
        break;
      case 'rejected':
        await outbox.markRejected(
          record.mutation_id,
          result.error ?? { code: 'REJECTED', message: 'The server refused this change.' },
        );
        break;
    }
  }

  private async pull(): Promise<void> {
    const projectIds = await this.deps.projectIds();
    for (const projectId of projectIds) {
      let cursor = await getCursor(projectId);
      for (let page = 0; page < MAX_PULL_PAGES_PER_PROJECT; page += 1) {
        let response;
        try {
          response = await this.deps.api.pull(projectId, cursor, PULL_PAGE_SIZE);
        } catch (error) {
          // Lost access to one project must not block the others.
          if (isApiError(error) && (error.status === 403 || error.status === 404)) break;
          throw error;
        }
        const { changes, next_cursor: nextCursor, has_more: hasMore } = response;
        // Apply the page and advance the cursor atomically: a crash mid-page
        // re-pulls the page instead of skipping it.
        await db.transaction('rw', [...applierTables(), db.sync_cursors], async () => {
          for (const change of changes) await applyChange(change, projectId);
          await setCursor(projectId, nextCursor);
        });
        cursor = nextCursor;
        if (!hasMore || !nextCursor) break;
      }
      // Messages stay bounded on the device (module 11): the latest 200 per
      // conversation from the last 30 days; unsent ones are always kept.
      await pruneMessages(projectId);
    }
  }

  // -- triggers ------------------------------------------------------------

  /** Install the automatic triggers. Returns a cleanup function. */
  start({ intervalMs = 60_000 }: { intervalMs?: number } = {}): () => void {
    this.started = true;
    void this.hydrate().then(() => this.run());

    const onOnline = () => {
      this.setConnectivity('online');
      void this.run();
    };
    const onOffline = () => this.setConnectivity('offline');
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.run();
    };
    const unsubscribeOutbox = outbox.subscribeToOutbox((change) => {
      void this.refreshCounts();
      if (change === 'work') this.schedule();
    });
    const unsubscribeMedia = subscribeToMediaQueue((change) => {
      void this.refreshCounts();
      if (change === 'work') this.schedule(500);
    });
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void this.run();
    }, intervalMs);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      unsubscribeOutbox();
      unsubscribeMedia();
      clearInterval(timer);
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.started = false;
    };
  }
}

export function useSyncSnapshot(engine: SyncEngine): SyncSnapshot {
  return useSyncExternalStore(engine.subscribe, engine.getSnapshot, engine.getSnapshot);
}
